import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { AuthProvider } from "./contexts/AuthContext";
import ProtectedRoute from "./components/ProtectedRoute";
import ErrorBoundary from "./ErrorBoundary";
import ScrollToTop from "./components/ScrollToTop/ScrollToTop";
import useErrorHandler from "./hooks/useErrorHandler";

import SignIn from "./Login";
import Dashboard from "./pages/Dashboard";
import Clients from "./pages/Clients/Clients";
import Enquiries from "./pages/Enquiries/Enquiries";
import Productions from "./pages/Productions/Productions";
import Leads from "./pages/Leads/Leads";
import OperationStages from "./pages/OperationStages/OperationStages";
import Teams from "./pages/Teams/Teams";
import Error500 from "./pages/Error500";
import AppLayout from "./AppLayout";

import "./App.scss";
import DashboardWithTutorial from "./pages/DashboardWithTutorial";
import Updates from "./pages/Updates/Updates";
import OverviewReport from "./pages/Reports/OverviewReport";
import ClientReport from "./pages/Reports/ClientReport";
import LeadReport from "./pages/Reports/LeadReport";
import EnquiryReport from "./pages/Reports/EnquiryReport";
import OperationReport from "./pages/Reports/OperationReport";
import CompletedOperationsReport from "./pages/Reports/CompletedOperationsReport";
import WarehouseStockReport from "./pages/Reports/WarehouseStockReport";

import { MODULES } from "./config/authorizationConfig";

function App() {
  useErrorHandler();

  return (
    <>
      <Router>
        <AuthProvider>
          <ErrorBoundary fallback={<Error500 />}>
            <ScrollToTop />

            <Routes>
              <Route path="/login" element={<SignIn />} />

              {/* DASHBOARD - role based */}
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute
                    roles={["ROLE_EMPLOYEE", "ROLE_HR", "ROLE_MANAGER"]}
                  >
                    <AppLayout>
                      <DashboardWithTutorial>
                        <Dashboard />
                      </DashboardWithTutorial>
                    </AppLayout>
                  </ProtectedRoute>
                }
              />

              {/* CRM ROUTES */}
              <Route
                path="/leads"
                element={
                  <ProtectedRoute
                    roles={["ROLE_SALES"]}
                    module={MODULES.CRM}
                    feature="CRM_LEADS"
                  >
                    <AppLayout>
                      <Leads />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/clients"
                element={
                  <ProtectedRoute
                    roles={["ROLE_SALES"]}
                    module={MODULES.CRM}
                    feature="CRM_CLIENTS"
                  >
                    <AppLayout>
                      <Clients />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/enquiries"
                element={
                  <ProtectedRoute
                    roles={["ROLE_SALES"]}
                    module={MODULES.CRM}
                    feature="CRM_ENQUIRIES"
                  >
                    <AppLayout>
                      <Enquiries />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />

              {/* ERP ROUTES */}
              <Route
                path="/productions"
                element={
                  <ProtectedRoute module={MODULES.ERP} feature="ERP_OPERATIONS">
                    <AppLayout>
                      <Productions />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/operations"
                element={
                  <ProtectedRoute roles={["ROLE_OPERATIONS"]}>
                    <AppLayout>
                      <OperationStages />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />

              {/* TEAMS (CRM module - feature based) */}
              <Route
                path="/team/*"
                element={
                  <ProtectedRoute
                    module={MODULES.CRM}
                    feature="CRM_TEAM_ENQUIRY"
                  >
                    <AppLayout>
                      <Teams />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />

              {[
                ["overview", ["ROLE_SALES-MANAGER"], OverviewReport],
                ["client", ["ROLE_SALES-MANAGER"], ClientReport],
                ["lead", ["ROLE_SALES-MANAGER"], LeadReport],
                ["enquiry", ["ROLE_SALES-MANAGER"], EnquiryReport],
                ["operation", ["ROLE_PROD-MANAGER"], OperationReport],
                ["completed", ["ROLE_PROD-MANAGER"], CompletedOperationsReport],
                [
                  "warehouse",
                  [
                    "ROLE_PROD-MANAGER",
                    "ROLE_INV-MANAGER",
                    "ROLE_INVENTORY-MANAGER",
                  ],
                  WarehouseStockReport,
                ],
              ].map(([reportType, roles, ReportPage]) => (
                <Route
                  key={reportType}
                  path={`/reports/${reportType}`}
                  element={
                    <ProtectedRoute roles={roles}>
                      <AppLayout>
                        <ReportPage />
                      </AppLayout>
                    </ProtectedRoute>
                  }
                />
              ))}

              {/* WHAT'S NEW - any signed-in CRM user */}
              <Route
                path="/updates"
                element={
                  <ProtectedRoute
                    roles={[
                      "ROLE_SALES",
                      "ROLE_SALES-MANAGER",
                      "ROLE_PROD-MANAGER",
                      "ROLE_INV-MANAGER",
                      "ROLE_INVENTORY-MANAGER",
                      "ROLE_OPERATIONS",
                    ]}
                  >
                    <AppLayout>
                      <Updates />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />

              <Route path="/error-500" element={<Error500 />} />
              <Route path="/" element={<Navigate to="/login" replace />} />
              <Route path="*" element={<Navigate to="/login" replace />} />
            </Routes>
          </ErrorBoundary>
        </AuthProvider>
      </Router>

      <ToastContainer
        position="top-right"
        autoClose={4000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        draggable
        pauseOnHover
        theme="light"
        style={{ zIndex: 9999999 }}
      />
    </>
  );
}

export default App;
